import { ConfirmationDialog } from "@/common/modal/userCancelActionWarning";
import Button from "@/common/ui/Button";
import { RoundLoader } from "@/common/ui/Loader";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import ConfirmationModal from "@/components/common/modal/ConfirmationModal";
import UpdateTempPassword from "@/components/dashboard/modal/UpdateTempPassword";
import UploadProfileModal from "@/components/dashboard/setting/profile/modal/UploadProfileModal";
import ProfileEdit from "@/components/dashboard/setting/profile/ProfileEdit";
import ProfileView from "@/components/dashboard/setting/profile/ProfileView";
import { FormRenderRef } from "@/components/Form/Renderer/RenderFormWithRef";
import { useDebounce } from "@/hooks/useDebounce";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import { updateMyProfile, useProfileInfo } from "@/services/common/profile.services";
import { checkAuth } from "@/services/common/user.services";
import { loginSuccessAction } from "@/store/actions/login.actions";
import { dashboardSelector } from "@/store/features/dashboardSlice";
import { loginSelector } from "@/store/features/loginSlice";
import { useCountries } from "@/store/hooks/useCountries";
import { useAppDispatch } from "@/store/store";
import { MODE } from "@/types/global";
import { CANCEL_DIRTY_FORM } from "@/utils/constant/dashboard.constant";
import { MyProfileConfig } from "@/utils/constant/formConfig.constant";
import { toastMessage } from "@/utils/helper/myToast";
import { requestUserConfirmation } from "@/utils/helper/promise";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

const modalConfig: ModalConfig = {
  uploadProfileImage: {
    component: UploadProfileModal,
    title: "Upload image",
    type: "side",
  },
  userConfirmation: {
    component: ConfirmationModal,
    title: "Remove File",
    modalClassName: "w-[330px]",
  },
  closeWarningModal: {
    component: ConfirmationDialog,
    title: "Discard changes",
    modalClassName: "w-[330px]",
  },
  updateTempPassword: {
    component: UpdateTempPassword,
    title: "Set new password",
    modalClassName: "!max-w-[330px]",
  },
};

interface ProfilePageProps {
  openModal: (modalKey: string, props?: any) => void;
  closeModal: () => void;
  activeModal: string;
  updateAndFetchProfile: (data: any) => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ openModal, activeModal, closeModal }) => {
  const [mode, setMode] = useState<MODE>("view");
  const { action } = useSelector(dashboardSelector);
  const { currentUser } = useSelector(loginSelector);
  const formRef: any = useRef<FormRenderRef>(null);
  const dispatch = useAppDispatch();
  const { data, isLoading, refetch }: any = useProfileInfo({ key: "my-profile" });
  const { isLoading: countryIsLoading } = useCountries({ autoFetch: true });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedSubmit = useDebounce(async () => {
    if (!formRef.current) return;
    try {
      await formRef.current.submitForm();
      setTimeout(async () => {
        const res: any = await checkAuth();
        if (res.status === 200) {
          const { data } = res.data;
          dispatch(loginSuccessAction(data));
        }
      }, 1500);
    } catch (error) {
      console.error("Form submission failed:", error);
    }
  }, 300);

  const handleSubmit = () => {
    debouncedSubmit();
  };

  const updateAndFetchProfile = async (payload: any) => {
    try {
      const res: any = await updateMyProfile(payload);
      if (res?.status === 200) {
        const { message, isError, showMessage } = res.data;
        return showMessage && toastMessage(message, isError);
      } else {
        console.log("Error", res);
        const { message, isError } = res?.response?.data || {};
        if (message) {
          return toastMessage(message, isError);
        }
        if (res.message) {
          return toastMessage(res.message, true);
        }
      }
    } catch (err) {
      console.log({ err });
    }
  };

  const handleChangeViewMode = async () => {
    if (action && action === "FORM_FILL") {
      try {
        await requestUserConfirmation({
          openModal,
          closeModal,
          modalKey: "closeWarningModal",
          modalProps: CANCEL_DIRTY_FORM,
        });
        closeModal();
        setMode("view");
      } catch (err) {
        console.log(err);
      }
    } else {
      setMode("view");
    }
  };

  useEffect(() => {
    if (!activeModal) {
      refetch();
    }
  }, [activeModal, mode]);

  const [isDirty, setIsDirty] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      const dirty = !!formRef.current && Object.keys(formRef.current.dirtyFields || {}).length > 0;
      setIsDirty(dirty);
    }, 300);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (currentUser?.isTempPass === "true" && (currentUser?.role === "User" || Boolean(currentUser?.isCloneUserRole))) {
      openModal("updateTempPassword");
    }
  }, [currentUser]);

  if (isLoading || countryIsLoading) return <RoundLoader />;

  return (
    <div className="px-4 overflow-auto h-full">
      <SideBarContentHeader heading="Profile">
        {mode === "edit" ? (
          <div className=" flex gap-x-4">
            <Button variant="secondary" size="small" onClick={handleChangeViewMode}>
              Cancel
            </Button>
            <Button variant="primary" size="small" onClick={handleSubmit} isLoading={isSubmitting} disabled={isSubmitting || isLoading || !isDirty}>
              Save
            </Button>
          </div>
        ) : (
          <>
            <Button variant="primary" size="small" onClick={() => setMode("edit")}>
              Edit profile
            </Button>
          </>
        )}
      </SideBarContentHeader>
      {mode === "edit" ? (
        <ProfileEdit
          ref={formRef}
          formConfig={MyProfileConfig}
          openModal={openModal}
          data={data}
          updateAndFetchProfile={updateAndFetchProfile}
          setIsSubmitting={setIsSubmitting}
          setMode={setMode}
          updateMyProfile={updateMyProfile}
          profile_key="profile_pic_path"
          nameKey="fullName"
        />
      ) : (
        <ProfileView
          nameKey="fullName"
          profilePicKey="profile_pic_path"
          formConfig={MyProfileConfig}
          data={data}
          openModal={openModal}
          updateAndFetchProfile={updateAndFetchProfile}
        />
      )}
    </div>
  );
};

export const ProfilePageWithModal = withModal(ProfilePage, modalConfig);
